import type { ViewjobOneGraphModel } from "../../../viewjobOneGraphModel";

export interface StarterChip {
  /** Stable id used for job-data gating (RN-7501 / RN-7548) and to key the
   *  translation resolvers in `dynamicConvoStarterStrings`. */
  id: DynamicConvoStarterId;
  /** English source string. Logged verbatim as the Signals impression key
   *  (locale-independent); the displayed/tapped text is resolved by
   *  `getDynamicConvoStarterLabel`. */
  label: string;
  /** English source for the tap starter message. The localized value (and the
   *  fallback to `label`) is produced by `getDynamicConvoStarterMessage`. */
  message?: string;
}

export type DynamicConvoStarterId =
  | "roleDetails"
  | "qualifications"
  | "hourlyPay"
  | "yearlyPay"
  | "typicalPay"
  | "workSchedule"
  | "location"
  | "remote"
  | "fullTime"
  | "datePosted"
  | "acceptingApplications"
  | "differentQuestion";

/**
 * Snapshot of View Job fields that gate specific starters
 * (RN-7501 / RN-7548). Built from the OneGraph model — no extra network calls
 * and no DocService taxonomy SUID decoding.
 */
export interface DynamicConvoStarterAvailability {
  hasSalary: boolean;
  /** App salary type from unitOfWork, e.g. HOURLY / YEARLY. */
  salaryType?: string;
  hasJobLocation: boolean;
  hasDatePublished: boolean;
  /** Job-type labels from jobTypeAndShiftSchedule / formatted job.jobType. */
  hasJobType: boolean;
  /** Shift/schedule strings from jobTypeAndShiftSchedule. */
  hasShiftAndSchedule: boolean;
  /** Remote work model or remoteLocation signal on the job. */
  hasRemoteInfo: boolean;
  /** Job is still open (`isExpired` is not true). */
  isAcceptingApplications: boolean;
}

/** Always pinned last among the visible DynamicConvoStarters. */
export const DIFFERENT_QUESTION_CHIP: StarterChip = {
  id: "differentQuestion",
  label: "I have a different question",
  message: "I have a different question about this job",
};

/**
 * Full conversation-starter pool for DynamicConvoStarters. Individual chips
 * are filtered by {@link getEligibleDynamicConvoStarters} using job-data
 * availability, then shuffled and truncated each time View Job opens.
 */
export const DYNAMIC_CONVO_STARTER_POOL: StarterChip[] = [
  { id: "roleDetails", label: "What would I be doing?" },
  { id: "qualifications", label: "What experience do I need?" },
  { id: "hourlyPay", label: "What is the hourly pay?" },
  { id: "yearlyPay", label: "What is the yearly pay?" },
  { id: "typicalPay", label: "What is the typical pay for jobs like this?" },
  { id: "workSchedule", label: "What is the work schedule?" },
  { id: "location", label: "Where is this job located?" },
  { id: "remote", label: "Is this job fully remote?" },
  { id: "fullTime", label: "Is this a full-time role?" },
  { id: "datePosted", label: "When was this job posted?" },
  { id: "acceptingApplications", label: "Is the employer still accepting applications?" },
];

/** Total chips shown when enough eligible starters exist (4 + pinned). */
export const DYNAMIC_CONVO_STARTERS_VISIBLE_COUNT = 5;

const ROTATING_VISIBLE_COUNT = DYNAMIC_CONVO_STARTERS_VISIBLE_COUNT - 1;

export const shuffleChips = <T>(chips: T[]): T[] => {
  const result = [...chips];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const hasJobLocationData = (model: ViewjobOneGraphModel | undefined): boolean => {
  const location = model?.job?.location;
  return !!(
    location?.formattedStreetAddress ||
    location?.city ||
    location?.postalCode ||
    model?.job?.formattedLocation ||
    model?.job?.jobCountry
  );
};

const hasNonEmptyStrings = (values: ReadonlyArray<string> | null | undefined): boolean =>
  !!values && values.some((value) => value.trim().length > 0);

const hasJobTypeData = (model: ViewjobOneGraphModel | undefined): boolean => {
  if (model?.job?.jobType?.trim()) {
    return true;
  }
  return hasNonEmptyStrings(
    model?.structuredDataJobSummaryModel?.jobTypeAndShiftSectionApp?.jobType,
  );
};

const hasShiftAndScheduleData = (model: ViewjobOneGraphModel | undefined): boolean => {
  const summary = model?.structuredDataJobSummaryModel;
  return (
    hasNonEmptyStrings(summary?.shiftAndSchedule) ||
    hasNonEmptyStrings(summary?.jobTypeAndShiftSectionApp?.shiftAndSchedule)
  );
};

const hasRemoteInfoData = (model: ViewjobOneGraphModel | undefined): boolean => {
  const remoteWorkModel = model?.job?.remoteWorkModel;
  if (remoteWorkModel?.type || remoteWorkModel?.text?.trim()) {
    return true;
  }
  return model?.job?.remoteLocation === true;
};

export const buildDynamicConvoStarterAvailability = (
  model: ViewjobOneGraphModel | undefined,
): DynamicConvoStarterAvailability => {
  const salaryInfo = model?.compensation?.salaryInfoModel;
  return {
    hasSalary: !!salaryInfo,
    salaryType: salaryInfo?.salaryType,
    hasJobLocation: hasJobLocationData(model),
    hasDatePublished: typeof model?.job?.datePublished === "number",
    hasJobType: hasJobTypeData(model),
    hasShiftAndSchedule: hasShiftAndScheduleData(model),
    hasRemoteInfo: hasRemoteInfoData(model),
    isAcceptingApplications: model?.job?.isExpired !== true,
  };
};

export const isDynamicConvoStarterEligible = (
  chip: StarterChip,
  availability: DynamicConvoStarterAvailability,
): boolean => {
  switch (chip.id) {
    case "hourlyPay":
      return availability.hasSalary && availability.salaryType === "HOURLY";
    case "yearlyPay":
      return availability.hasSalary && availability.salaryType === "YEARLY";
    case "typicalPay":
      return !availability.hasSalary;
    case "location":
      return availability.hasJobLocation;
    case "datePosted":
      return availability.hasDatePublished;
    case "workSchedule":
      return availability.hasShiftAndSchedule;
    case "remote":
      return availability.hasRemoteInfo;
    case "fullTime":
      return availability.hasJobType;
    case "acceptingApplications":
      return availability.isAcceptingApplications;
    case "roleDetails":
    case "qualifications":
      return true;
    default:
      return true;
  }
};

export const getEligibleDynamicConvoStarters = (
  availability: DynamicConvoStarterAvailability,
  pool: StarterChip[] = DYNAMIC_CONVO_STARTER_POOL,
): StarterChip[] => pool.filter((chip) => isDynamicConvoStarterEligible(chip, availability));

/**
 * Picks a fresh set of DynamicConvoStarters for one View Job open:
 * shuffle the (already filtered) pool, take up to 4, then always append
 * "I have a different question".
 */
export const selectVisibleDynamicConvoStarters = (
  pool: StarterChip[] = DYNAMIC_CONVO_STARTER_POOL,
  differentQuestion: StarterChip = DIFFERENT_QUESTION_CHIP,
): StarterChip[] => {
  const rotating = shuffleChips(pool).slice(0, ROTATING_VISIBLE_COUNT);
  return [...rotating, differentQuestion];
};

/**
 * Splits chips into two rows for a horizontal carousel. Each row packs its own
 * chips left-to-right independently, so a short chip never leaves a gap waiting
 * on a longer chip in the row above. A pinned-last chip ends the bottom row.
 */
export const packChipsIntoTwoRows = <T>(chips: T[]): [T[], T[]] => {
  const topCount = Math.ceil(chips.length / 2);
  return [chips.slice(0, topCount), chips.slice(topCount)];
};

/** One chip paired with its 1-based row and in-row position. */
export interface ChipLayout<T> {
  item: T;
  row: number;
  position: number;
}

/**
 * Flattens the two packed rows into a positional layout of
 * `{ item, row, position }` (1-based), used by impression logging to tag each
 * starter with its row/placement. The render computes the same numbers inline
 * from the same packed `rows`.
 */
export const layoutPackedChips = <T>(rows: [T[], T[]]): ChipLayout<T>[] =>
  rows.flatMap((rowChips, rowIndex) =>
    rowChips.map((item, pos) => ({
      item,
      row: rowIndex + 1,
      position: pos + 1,
    })),
  );
